import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { analyticsService } from '../services/analyticsService';
import Skeleton from '../components/ui/Skeleton';
import ProgressBar from '../components/ui/ProgressBar';
import styles from './Analytics.module.css';

const COHORT_WEEKS = ['W0', 'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7'];
const PLATFORM_COLORS = { Android: '#B94040', iOS: '#5080B8', Web: '#5A9E78' };

class Analytics extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, data: null };
  }

  componentDidMount() {
    analyticsService.getAnalyticsPage()
      .then((data) => this.setState({ loading: false, data }))
      .catch(() => this.setState({ loading: false }));
  }

  render() {
    const { loading, data } = this.state;

    if (loading || !data) {
      return (
        <div className={styles.grid2}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={styles.card}>
              <Skeleton variant="text" width={160} height={16} style={{ margin: '14px 16px 8px' }} />
              <Skeleton variant="rect" width="calc(100% - 32px)" height={180} style={{ margin: '0 16px 16px', borderRadius: 8 }} />
            </div>
          ))}
        </div>
      );
    }

    const { MONTHS, MAU, PLATFORM_DATA, RETENTION_COHORT, TOP_EVENTS, APP_VERSIONS } = data;
    const platformTotal = PLATFORM_DATA.reduce((sum, p) => sum + p.count, 0) || 1;

    const mauData = MONTHS.map((m, i) => ({ m, v: MAU[i] }));
    const evtData = TOP_EVENTS.labels.slice(0, 8).map((l, i) => ({ l: l.replace(/_/g, ' '), v: TOP_EVENTS.data[i] }));
    const cohortData = COHORT_WEEKS.map((w, i) => ({ w, v: RETENTION_COHORT[i] }));

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Analytics</div>
            <div className={styles.pageSub}>Behavioral insights · retention cohorts · platform breakdown</div>
          </div>
          <button className={styles.exportBtn}>⬇ Export</button>
        </div>

        <div className={styles.grid2}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Platform Split</div>
              <div className={styles.cardSub}>Users by platform</div>
            </div>
            <div className={styles.platWrap}>
              {PLATFORM_DATA.map((p) => (
                <div key={p.platform} className={styles.platRow}>
                  <span className={styles.platName}>{p.platform}</span>
                  <div className={styles.platBar}>
                    <ProgressBar
                      value={Math.round((p.count / platformTotal) * 100)}
                      max={100}
                      showValue
                      size="md"
                      color={PLATFORM_COLORS[p.platform] || '#888'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Retention Cohort</div>
              <div className={styles.cardSub}>Blended across all cohorts, week 0–7</div>
            </div>
            <div className={styles.cardBody}>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={cohortData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#B94040" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#B94040" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                  <XAxis dataKey="w" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [v === null ? 'not enough data yet' : v + '%', 'Retained']}
                  />
                  <Area type="monotone" dataKey="v" stroke="#B94040" strokeWidth={2} fill="url(#retGrad)" dot={false} connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Top Events</div>
              <div className={styles.cardSub}>All-time · by frequency</div>
            </div>
            <div className={styles.cardBody}>
              <ResponsiveContainer width="100%" height={215}>
                <BarChart data={evtData} layout="vertical" margin={{ top: 0, right: 8, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="l" type="category" tick={{ fill: 'var(--t2)', fontSize: 10 }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="v" name="Events" fill="var(--red)" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>App Version Distribution</div>
              <div className={styles.cardSub}>Not tracked yet</div>
            </div>
            <div className={styles.verList}>
              {APP_VERSIONS.length === 0
                ? <div className={styles.cardSub} style={{ padding: 16 }}>No app_version property recorded on events yet.</div>
                : APP_VERSIONS.map((v) => (
                    <div key={v.v} className={styles.verRow}>
                      <span className={styles.verLabel}>{v.v}</span>
                      <div className={styles.verBar}>
                        <ProgressBar value={v.p} max={52} size="sm" color={v.col} />
                      </div>
                      <span className={styles.verPct}>{v.p}%</span>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Monthly Active Users</div>
            <div className={styles.cardSub}>12-month trend</div>
          </div>
          <div className={styles.cardBody}>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={mauData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="mauGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B94040" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#B94040" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                <XAxis dataKey="m" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="v" stroke="#B94040" strokeWidth={2} fill="url(#mauGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </>
    );
  }
}

export default Analytics;
